import React, { useState, useEffect, useContext } from 'react';
import { isTokenExpiringSoon } from './tokenUtils';
import AdminTable from './AdminTable';

interface MemberDashboardProps {
  apiBaseUrl: string;
  accessToken: string | null;
  onAddMember: () => void;
  onEditMember: (member: import('./EditMemberForm').Member) => void;
  onLogout: () => void;
}

interface MemberDashboardPropsWithRefresh extends MemberDashboardProps {
  refreshFlag?: number;
  loggedInUserId?: number;
  userRoles?: string[];
  userSports?: string[]; // Sports the user has access to (for sports admin filtering)
}

const MemberDashboard: React.FC<MemberDashboardPropsWithRefresh> = ({ apiBaseUrl, accessToken, onAddMember, onEditMember, onLogout, refreshFlag, loggedInUserId, userRoles, userSports }) => {
  // Helper to refresh token if expiring soon
  const refreshTokenIfNeeded = async (token: string | null): Promise<string | null> => {
    if (!token) return null;
    if (!isTokenExpiringSoon(token, 600)) return token;
    try {
      const res = await fetch(`${apiBaseUrl}/oauth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        return data.access_token;
      }
    } catch {}
    return token;
  };

  const fetchWithRefresh = async (input: RequestInfo, init: RequestInit = {}) => {
    const token = await refreshTokenIfNeeded(accessToken);
    const headers = {
      ...(init.headers || {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
    return fetch(input, { ...init, headers });
  };

  const [members, setMembers] = useState<import('./EditMemberForm').Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);

  // Extract sports from sports_admin roles
  function getUserSports(roles: string[] | undefined): string[] {
    if (!roles || !Array.isArray(roles)) return [];
    return roles
      .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
      .map(r => r.replace('sports_admin_', '').toLowerCase());
  }

  // Check if user can add members (admin or trustee only)
  function canAddMember(roles: string[] | undefined): boolean {
    if (!roles || !Array.isArray(roles)) return false;
    const rolesLower = roles.map(r => r.toLowerCase());
    return rolesLower.includes('admin') || rolesLower.includes('trustee');
  }

  // Filter members for sports admins (client-side safety net; backend already filters)
  function filterMembersForSportsAdmin(members: import('./EditMemberForm').Member[]): import('./EditMemberForm').Member[] {
    // Admins and trustees see ALL members — do not filter
    const rolesLower = (userRoles || []).map(r => r.toLowerCase());
    if (rolesLower.includes('admin') || rolesLower.includes('trustee')) {
      return members;
    }
    const sports = userSports || getUserSports(userRoles);
    if (sports.length === 0) return members;
    const sportsLower = sports.map(s => s.toLowerCase());
    return members.filter(m => {
      const memberSportsLower = (m.sports || []).map(s => s.name.toLowerCase());
      return memberSportsLower.some(ms => sportsLower.includes(ms));
    });
  }

  // Fetch all members
  const fetchMembers = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithRefresh(`${apiBaseUrl}/members`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        // Filter for sports admins (backend already does this, client-side ensures consistency)
        const filteredMembers = filterMembersForSportsAdmin(data);
        setMembers(filteredMembers);
      } else {
        setError(data.message || 'Failed to fetch members');
      }
    } catch (err) {
      setError('Network error or API is not running.');
    } finally {
      setLoading(false);
    }
  };

  // Search members
  const searchMembers = async (q: string): Promise<void> => {
    setSearching(true);
    setError(null);
    try {
      const response = await fetchWithRefresh(`${apiBaseUrl}/members/search?q=${encodeURIComponent(q)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        // Filter search results for sports admins
        const filteredMembers = filterMembersForSportsAdmin(data);
        setMembers(filteredMembers);
      } else {
        setError(data.message || 'Failed to search members');
      }
    } catch (err) {
      setError('Network error or API is not running.');
    } finally {
      setSearching(false);
    }
  };

  // Delete a member
  const handleDeleteMember = async (memberId: number): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      const response = await fetchWithRefresh(`${apiBaseUrl}/members/${memberId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        alert('Member deleted successfully!');
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('refreshMembers'));
        }
      } else {
        const data = await response.json();
        alert(`Failed to delete member: ${data.message || response.statusText}`);
      }
    } catch (error) {
      alert('An error occurred while deleting the member.');
    }
  };

  // Refresh members when refreshFlag changes
  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line
  }, [refreshFlag]);

  // Listen for refreshMembers event to update table after deletion
  useEffect(() => {
    const handleRefreshMembers = () => {
      fetchMembers();
    };
    window.addEventListener('refreshMembers', handleRefreshMembers);
    return () => {
      window.removeEventListener('refreshMembers', handleRefreshMembers);
    };
    // eslint-disable-next-line
  }, [apiBaseUrl, accessToken]);

  useEffect(() => {
    let debounce: ReturnType<typeof setTimeout>;
    if (!search) {
      fetchMembers();
    } else {
      debounce = setTimeout(() => {
        searchMembers(search);
      }, 400);
    }
    return () => { if (debounce) clearTimeout(debounce); };
    // eslint-disable-next-line
  }, [apiBaseUrl, accessToken, search]);

  if (loading || searching) return <div className="text-center py-8">Loading members...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error: {error}</div>;

  return (
    <div className="relative" data-testid="member-dashboard">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Club Members</h2>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <input
          type="text"
          className="w-full sm:w-72 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button
          onClick={onAddMember}
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-lg transition duration-300 ease-in-out"
          disabled={!canAddMember(userRoles)}
        >
          Add New Member
        </button>
      </div>
      <AdminTable
        members={members}
        onEditMember={onEditMember}
        onDeleteMember={handleDeleteMember}
        loggedInUserId={loggedInUserId}
        userRoles={userRoles}
        accessToken={accessToken}
      />
    </div>
  );
};

export default MemberDashboard;
